import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Mission, { isDefaultMissionNameStructure } from "../../interfaces/robot/missions/mission";
import { getNavigatorDescription } from "../../interfaces/robot/navigator";
import { getColorFromString } from "../../utils/mission-color";
import RoomData from "../../interfaces/robot/rooms/room-data";

const MissionSelect: React.FC<{
  missionId: string;
  missions: Mission[];
  filteredRooms?: RoomData[];
  currentRoomId?: string | null;
  onMissionChange: (newId: string) => void;
  missionFilter: (mission: Mission) => boolean;
  missionSort?: (a: Mission, b: Mission) => number;
  onAddMissionNavigate?: () => void;
}> = ({ missionId, missions, filteredRooms, currentRoomId, onMissionChange, missionFilter, missionSort }) => {
  const { t } = useTranslation();

  const filteredMissions = missions.filter(missionFilter);
  if (missionSort != null) {
    filteredMissions.sort(missionSort);
  }

  const roomsWithMissions = filteredRooms
    ?.filter((room) => filteredMissions.some((m) => m.room === room.id))
    .sort((a, b) => {
      if (a.id === currentRoomId) return -1;
      if (b.id === currentRoomId) return 1;
      return 0;
    });

  const hasMultipleRooms = roomsWithMissions != null && roomsWithMissions.length > 1;

  const handleToggleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue != null) onMissionChange(newValue);
  };

  const getMissionLabel = (mission: Mission): string => {
    if (mission.room != null) {
      return isDefaultMissionNameStructure(mission.name)
        ? mission.name
        : `${mission.name} - ${getNavigatorDescription(mission.mode)}`;
    }
    return getNavigatorDescription(mission.mode);
  };

  const renderMissionCards = (missionList: Mission[]) =>
    missionList.map((mission) => (
      <ToggleButton
        key={mission.id}
        value={mission.id}
        sx={(theme) => ({
          borderLeft: `${theme.spacing(1)} ${getColorFromString(mission.color, "0.3")} solid !important`,
          color: missionId === mission.id ? `${theme.palette.primary.main}CC !important` : "GrayText",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          padding: 1,
          fontWeight: "bold",
          fontSize: "0.87rem",
          textTransform: "none",
          textAlign: "left",
        })}
      >
        {getMissionLabel(mission)}
      </ToggleButton>
    ));

  const renderRoomSection = (roomKey: string, roomMissions: Mission[], label?: string) => {
    const isCurrentRoom = roomKey === currentRoomId;
    return (
      <Box key={roomKey} sx={{ pt: 1 }}>
        {label && (
          <Typography
            variant="caption"
            sx={(theme) => ({
              pl: 0.5,
              fontWeight: isCurrentRoom ? "bold" : "normal",
              color: isCurrentRoom ? theme.palette.primary.main : "text.secondary",
            })}
          >
            {label}
          </Typography>
        )}
        <ToggleButtonGroup
          value={missionId}
          exclusive
          onChange={handleToggleChange}
          size="small"
          sx={(theme) => ({
            width: "100%",
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
            minHeight: theme.spacing(5),
          })}
        >
          {renderMissionCards(roomMissions)}
        </ToggleButtonGroup>
      </Box>
    );
  };

  return (
    <Stack sx={{ pt: 1 }}>
      <Box sx={(theme) => ({ overflowY: "auto", maxHeight: theme.spacing(52) })}>
        {hasMultipleRooms
          ? roomsWithMissions?.map((room) => {
              const roomMissions = filteredMissions.filter((m) => m.room === room.id);
              if (roomMissions.length === 0) return null;
              return renderRoomSection(room.id, roomMissions, room.name);
            })
          : renderRoomSection("_", filteredMissions)}
      </Box>
      {missionId === "" && <FormHelperText error>{t("select-mission-to-continue")}</FormHelperText>}
    </Stack>
  );
};

export default MissionSelect;
